import { richTextToPlain } from "../common/rich-text/richText";
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FaTimes,
  FaExclamationCircle,
  FaComment,
  FaUserCheck,
  FaBell,
  FaTrash,
  FaEyeSlash,
  FaCheckDouble,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaRedo,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
  clearErrors,
} from "../Slices/NotificationsSlice";
import { useNotifications } from "../hooks/useNotifications";
import { callApi } from "../common/Apicall";
import { useNavigate } from "react-router-dom";
import { buildUrl } from "../common/navigation";
import moment from "moment";
import Swal from "sweetalert2"; // Import SweetAlert

export default function NotificationsDrawer({ isOpen, setIsOpen }) {
  const URL = process.env.REACT_APP_URL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    isFetchingMore,
    totalCount,
    lastFetchError,
  } = useSelector((state) => state.notifications);

  const { fetchNotifications, loadMoreNotifications, isFetching } =
    useNotifications();
  const notificationsEndRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(false);

  // Clear errors when drawer is opened/closed
  useEffect(() => {
    dispatch(clearErrors());
  }, [isOpen, dispatch]);

  // Fetch notifications from API
  useEffect(() => {
    if (isOpen) {
      fetchNotifications().catch(() => {
        // Error is already handled in fetchNotifications
      });
    }
  }, [isOpen]);

  // Setup scroll event listener for infinite scrolling
  useEffect(() => {
    const notificationsContainer = document.querySelector(
      ".notifications-container",
    );
    if (!notificationsContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = notificationsContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Load more when user is within 200px of the bottom
      if (
        distanceFromBottom < 200 &&
        hasMore &&
        !isFetchingMore &&
        !isFetching &&
        !lastFetchError
      ) {
        setIsNearBottom(true);
        loadMoreNotifications().catch(() => {
          // Error is already handled in loadMoreNotifications
        });
      } else {
        setIsNearBottom(false);
      }
    };

    notificationsContainer.addEventListener("scroll", handleScroll);
    return () =>
      notificationsContainer.removeEventListener("scroll", handleScroll);
  }, [
    hasMore,
    isFetchingMore,
    isFetching,
    loadMoreNotifications,
    lastFetchError,
  ]);

  // Scroll to bottom when new notifications are added
  useEffect(() => {
    if (notificationsEndRef.current && isNearBottom) {
      notificationsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [notifications, isNearBottom]);

  const handleMarkAsRead = async (id, showToast) => {
    try {
      await callApi(`${URL}/Notification/MarkAsRead/${id}`, "POST");
      dispatch(markAsRead(id));
      if (showToast) toast.success("Notification marked as read");
    } catch (error) {
      console.error("Error marking notification as read:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to mark as read";
      toast.error(errorMessage);

      // Revert the optimistic update in UI
      dispatch(clearErrors());
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await callApi(`${URL}/Notification/MarkAllAsRead`, "POST");
      dispatch(markAllAsRead());
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to mark all as read";
      toast.error(errorMessage);
    }
  };

  const handleDeleteNotification = async (id) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This notification will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await callApi(`${URL}/Notification/Clear/${id}`, "DELETE");
        dispatch(deleteNotification(id));
        toast.success("Notification deleted");
      } catch (error) {
        console.error("Error deleting notification:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to delete notification";
        toast.error(errorMessage);
      }
    }
  };

  const handleClearAllNotifications = async () => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "All your notifications will be permanently deleted. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, clear all!",
    });

    if (result.isConfirmed) {
      try {
        await callApi(`${URL}/Notification/ClearAll`, "DELETE");
        dispatch(clearAllNotifications());
        toast.success("All notifications cleared");
      } catch (error) {
        console.error("Error clearing notifications:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to clear notifications";
        toast.error(errorMessage);
      }
    }
  };

  const handleRetryFetch = () => {
    fetchNotifications().catch(() => {
      // Error is already handled in fetchNotifications
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "assignment":
        return <FaUserCheck className="text-indigo-500" />;
      case "status_change":
        return <FaExclamationCircle className="text-amber-500" />;
      case "comment":
        return <FaComment className="text-emerald-500" />;
      default:
        return <FaBell className="text-gray-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id, false);
    }
    // Navigate to the relevant ticket
    trigger(notification.ticketId, notification.ticketType);
  };

  const trigger = useCallback(
    (taskId, ticketType) => {
      let screenName = "SearchedTicket";
      const params = {};

      // Notifications open the BPR board for the ticket: Scrum tickets on BPR
      // ProSoftware Service Tickets, Support tickets on BPR Client/OPS
      // Tickets. Screen names rather than raw paths, per navigation.js.
      if (ticketType === "Scrum") {
        screenName = "BMRSoftwareTickets";
        // Land on the first tab ("Engineer" — VIEW_TABS.Engineer in
        // SoftwareTickets). That screen reads its active tab from the `tab`
        // query param, so without this it would restore whichever summary tab
        // was last open instead of the ticket list.
        params.tab = "Engineer";
      } else if (ticketType === "Support") {
        screenName = "BMRSearchedTicket";
      }

      // SearchedTicket and its BPR twin are the same component, so both take
      // the id in the string form it expects.
      params.taskId =
        screenName === "SearchedTicket" || screenName === "BMRSearchedTicket"
          ? String(taskId)
          : taskId;

      navigate(buildUrl(screenName, params));
      setIsOpen(false);
    },
    [navigate, setIsOpen],
  );

  const formatMessageWithBold = (message) => {
    const parts = richTextToPlain(message || "").split(/(\*[^*]+\*)/g);

    return parts.map((part, index) => {
      // Existing logic: text wrapped in * *
      const isAsteriskBold = part.startsWith("*") && part.endsWith("*");

      // New logic: bold text before "has commented", "has changed",
      // or "reacted to a"
      const match = part.match(
        /^(.*?)(?=\s+(?:has commented|has changed|reacted to a))/i,
      );

      if (isAsteriskBold) {
        return (
          <strong key={index} className="font-semibold">
            {part.slice(1, -1)}
          </strong>
        );
      }

      if (match && match[1]) {
        return (
          <React.Fragment key={index}>
            <strong className="font-semibold">{match[1]}</strong>
            {part.slice(match[1].length)}
          </React.Fragment>
        );
      }

      return part;
    });
  };

  return (
    <div>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-xl transform ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-out z-50`}
      >
        <div className="p-5 relative h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Notifications
              </h2>
              {unreadCount > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {unreadCount} unread{" "}
                  {unreadCount === 1 ? "notification" : "notifications"}
                </p>
              )}
              {totalCount > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {totalCount} total notifications
                </p>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {notifications.length > 0 && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="p-2 text-gray-500 hover:text-indigo-600 transition-colors rounded-full hover:bg-indigo-50"
                  title="Mark all as read"
                  disabled={isLoading}
                >
                  <FaCheckDouble />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAllNotifications}
                  className="p-2 text-gray-500 hover:text-red-600 transition-colors rounded-full hover:bg-red-50"
                  title="Clear all notifications"
                  disabled={isLoading}
                >
                  <FaTrash />
                </button>
              )}
              <button
                className="p-2 bg-gray-300 hover:text-gray-700 transition-colors rounded-full hover:bg-gray-400"
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Error State */}
          {lastFetchError && !isLoading && !isFetchingMore && (
            <div className="flex flex-col justify-center items-center py-12 px-4">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaExclamationTriangle className="text-2xl text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2 text-center">
                Failed to load notifications
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                {lastFetchError}
              </p>
              <button
                onClick={handleRetryFetch}
                className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                <FaRedo className="mr-2" />
                Try Again
              </button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !isFetchingMore && !lastFetchError ? (
            <div className="flex flex-col justify-center items-center py-12">
              <FaSpinner className="animate-spin text-indigo-500 text-xl" />
              <p className="text-gray-500">Loading notifications</p>
            </div>
          ) : notifications.length === 0 && !hasMore && !lastFetchError ? (
            <div className="text-center py-16 px-4">
              <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBell className="text-2xl text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">
                No notifications yet
              </h3>
              <p className="text-sm text-gray-500">
                We'll notify you when something arrives
              </p>
            </div>
          ) : !lastFetchError ? (
            <div className="notifications-container overflow-y-auto flex-grow -mr-2 pr-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg mb-2 transition-all duration-200 cursor-pointer ${
                    !notification.isRead
                      ? "bg-indigo-50 border-l-4 border-indigo-500"
                      : "bg-white hover:bg-gray-50"
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 text-lg flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="flex justify-between items-start">
                        <h3 className="font-medium text-gray-900 truncate">
                          {richTextToPlain(notification.title || "")}
                        </h3>
                        {!notification.isRead && (
                          <span className="bg-indigo-500 w-2 h-2 rounded-full flex-shrink-0 mt-2 ml-2"></span>
                        )}
                      </div>
                      <p
                        className="text-sm text-gray-600 mt-1 line-clamp-2 break-words"
                        title={
                          richTextToPlain(notification.message || "").length > 80
                            ? richTextToPlain(notification.message || "")
                            : ""
                        }
                      >
                        {formatMessageWithBold(notification.message)}
                      </p>

                      <div className="flex justify-between items-center mt-3">
                        <span className="text-xs text-gray-400">
                          {formatTime(
                            moment.utc(notification.createdAt).local(),
                          )}
                        </span>
                        <div className="flex space-x-3">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id, true);
                              }}
                              className="text-gray-400 hover:text-indigo-600 transition-colors"
                              title="Mark as read"
                            >
                              <FaCheckCircle />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete notification"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading more indicator */}
              {isFetchingMore && (
                <div className="flex justify-center py-4">
                  <FaSpinner className="animate-spin text-indigo-500 text-xl" />
                </div>
              )}

              {/* End of notifications message */}
              {!hasMore && notifications.length > 0 && (
                <div className="text-center py-4 text-sm text-gray-500">
                  You've reached the end of your notifications
                </div>
              )}

              <div ref={notificationsEndRef} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
